import { Injectable } from '@angular/core';
import { ListService } from 'bookraw/shared/services/list.service';
import { User, UserRealType } from 'bookraw/shared/models/user.model';
import { Observable } from 'rxjs';
import { ItemResponse } from 'bookraw/shared/models/api/item.response';
import { environment } from 'env/environment';
import { ListResponse } from 'bookraw/shared/models/api/list.response';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isArray } from 'rxjs/internal-compatibility';
import { BookrawAppModule } from 'bookraw/app.module';
import { AppLoaderService } from 'app/shared/services/app-loader/app-loader.service';
import { showErrorMessage } from 'bookraw/shared/helpers/errorHelper';
import { Book } from 'bookraw/shared/models/book.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class UsersService extends ListService<User> {

  url: string = '/api/admin/users';
  protected readonly loaderService: AppLoaderService;

  constructor(
    public http: HttpClient,
    private translateService: TranslateService,
  ) {
    super(http);
    this.loaderService = BookrawAppModule.injector.get(AppLoaderService);

  }

  assignHiddenTerms(data: { userIds: number[], taxonomyTermIds: number[] }): Observable<ItemResponse<User>> {
    return this.http.post<ItemResponse<User>>(environment.baseURL + this.url + '/assignHiddenTerms', data);
  }

  removeHiddenTerms( data: { userIds: number[], taxonomyTermIds: number[] }): Observable<ItemResponse<User>> {
    return this.http.put<ItemResponse<User>>(environment.baseURL + this.url + '/removeHiddenTerms', data);
  }

  assignBooks(data: { userIds: number[], bookIds: number[], startsAt: string, expiresAt: string }): Observable<ItemResponse<User>> {
    return this.http.post<ItemResponse<User>>(environment.baseURL + this.url + '/assignBooks', data);
  }

  unAssignBooks(data: { userIds: number[], bookIds: number[] }): Observable<ItemResponse<User>> {
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: data };
    return this.http.delete<ItemResponse<User>>(environment.baseURL + this.url + '/assignBooks', options);
  }

  assignPlaylists(data: { userIds: number[], playlistIds: number[], startsAt: string, expiresAt: string }): Observable<ItemResponse<User>> {
    return this.http.post<ItemResponse<User>>(environment.baseURL + this.url + '/assignPlaylists', data);
  }

  unAssignPlaylists(data: { userIds: number[], playlistIds: number[] }): Observable<ItemResponse<User>> {
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: data };
    return this.http.delete<ItemResponse<User>>(environment.baseURL + this.url + '/assignPlaylists', options);
  }

  assignLevel(data: { userIds: number[], taxonomyTermIds: number[] }): Observable<ItemResponse<User>> {
    return this.http.post<ItemResponse<User>>(environment.baseURL + this.url + '/assignLevel', data);
  }

  remindAssignment(data: { userId: number, bookIds: number[] }): Observable<ItemResponse<User>> {
    return this.http.post<ItemResponse<User>>(environment.baseURL + this.url + '/remindAssignment', data);
  }

  resetPassword(data: { email: string, password: string, token: string }) {
    return this.http.post(environment.baseURL + this.url + '/resetPassword', data);
  }

  adultConfirmation(data: { password: string, passwordAgain: string, confirmationHash: string }) {
    return this.http.post(environment.baseURL + this.url + '/adultConfirmation', data);
  }

  reGenerateAccessCode(userId: number) {
    return this.http.post(environment.baseURL + this.url + '/' + userId + '/accessCode', null);
  }

  inviteAdult(userId: number) {
    return this.http.post(environment.baseURL + this.url + '/' + userId + '/inviteAdult', null);
  }

  resendCreatePassword(userId: number) {
    return this.http.post(environment.baseURL + this.url + '/' + userId + '/resendCreatePassword', null);
  }

  getMe(): Observable<ItemResponse<User>> {
    return this.http.get<ItemResponse<User>>(environment.baseURL + this.url + '/me');
  }

  updateMe(data: User): Observable<ItemResponse<User>> {
    return this.http.put<ItemResponse<User>>(environment.baseURL + this.url + '/me', data);
  }

  forgotPassword(data: { email: string }) {
    // a backend a client alapján dönti el, hogy melyik felületre mutató linket küldje ki emailben
    return this.http.post(environment.baseURL + this.url + '/forgotPassword', { ...data, client: 'admin' });
  }

  importStudents(data: FormData) {
    return this.http.post(environment.baseURL + this.url + '/importStudents', data)
  }

  exportStudents(schoolId: string, userIds: string) {
    const params =  schoolId ? { schoolId, userIds } : { userIds }

    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/exportStudents' + this.queryParams.paramString, {
      headers, observe: 'response', params: params, responseType: 'blob'
    });
  }

  exportStudentCodes(schoolId: string, userIds: string, whitelabel: string|null = null) {
    const params =  schoolId ? { schoolId, userIds, whitelabel } : { userIds, whitelabel }

    const headers = new HttpHeaders().set('Accept', 'application/pdf');
    return this.http.get(environment.baseURL + this.url + '/exportStudentCodes' + this.queryParams.paramString, {
      headers, observe: 'response', params: params, responseType: 'blob'
    });
  }

  exportTeachers(schoolId: string, userIds: string) {
    const params =  schoolId ? { schoolId, userIds } : { userIds }

    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/exportTeachers', {
      headers, observe: 'response', params: params, responseType: 'blob'
    });
  }

  exportB2bUsers(data: { type: 'all' | 'student' | 'teacher', userIds: string }) {
    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/exportB2bUsers', {
      headers, observe: 'response', params: data, responseType: 'blob'
    });
  }

  exportB2cUsers(data: { userIds: string }) {
    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/exportB2cUsers', {
      headers, observe: 'response', params: data, responseType: 'blob'
    });
  }

  getTeachers(): Observable<ListResponse<User>> {
    return this.http.get<ListResponse<User>>(environment.baseURL + this.url, {
      params: { 'filters[realType]': UserRealType.TEACHER }
    });
  }

  importTeachers(data: FormData) {
    return this.http.post(environment.baseURL + this.url + '/importTeachers', data)
  }

  assignBooksToStudents(
    elements: { action: 'assign' | 'revoke', users: User | User[], expireDate: string | null, startDate: string | null },
    studentListDialog,
    selection,
    loading
  ) {
    const data = {
      expiresAt: elements.expireDate,
      startsAt: elements.startDate,
      userIds: isArray(elements.users) ? elements.users.map(stud => stud.id) : [ elements.users.id ],
      bookIds: [...selection.selected.map(book => book.id)]
    };

    this.loaderService.open( this.translateService.instant('general.in_progress') );

    if (elements.action === 'assign') {

      this.assignBooks(data).subscribe(() => {
        BookrawAppModule.showSuccessMessage( this.translateService.instant('book.assign_success') );
        this.loaderService.close();
        if (selection.selected.length === 1) studentListDialog.reloadList();
      }, (error) => {
        loading = false;
        showErrorMessage(error);
        this.loaderService.close();
      });
    } else if (elements.action === 'revoke') {
      this.unAssignBooks(data).subscribe(() => {
        BookrawAppModule.showSuccessMessage( this.translateService.instant('book.assign_remove_success') );
        this.loaderService.close();
        if (selection.selected.length === 1) studentListDialog.reloadList();
      }, (error) => {
        loading = false;
        showErrorMessage(error);
        this.loaderService.close();
      });
    }
  }

  assignToBooks(
    elements: {book: Book | Book[], expireDate: string, startDate: string},
    userIds,
    errorCallback,
    getItems,
    closeDialog,
    selection = null
  ) {
    const result = {
      success: false,
      multiple: isArray(elements.book)
    }

    const data = {
      expiresAt: elements.expireDate,
      startsAt: elements.startDate,
      userIds: userIds,
      bookIds: isArray(elements.book) ? elements.book.map(books => books.id) : [ elements.book.id ]
    };

    this.loaderService.open( this.translateService.instant('general.in_progress') );
    this.assignBooks(data).subscribe(() => {
      this.loaderService.close();
      BookrawAppModule.showSuccessMessage( this.translateService.instant('general.assign_success') );
      getItems();
      if (isArray(elements.book)) {
        closeDialog();
        if (selection) selection.clear();
      }
    }, error => {
      errorCallback(error)
      this.loaderService.close();
    });

    return result;
  }

  assignPlaylistsToStudents(
    elements: { action: 'assign' | 'revoke', users: User | User[], startDate: string | null, expireDate: string | null },
    studentListDialog,
    selection,
    loading
  ) {
    const data = {
      startsAt: elements.startDate,
      expiresAt: elements.expireDate,
      userIds: isArray(elements.users) ? elements.users.map(stud => stud.id) : [ elements.users.id ],
      playlistIds: [...selection.selected.map(playlist => playlist.id)]
    };

    this.loaderService.open( this.translateService.instant('general.in_progress') );

    if (elements.action === 'assign') {

      this.assignPlaylists(data).subscribe(() => {
        BookrawAppModule.showSuccessMessage('Playlist(s) assign successfully.');
        this.loaderService.close();
        if (selection.selected.length === 1) studentListDialog.reloadList();
      }, (error) => {
        loading = false;
        showErrorMessage(error);
        this.loaderService.close();
      });
    } else if (elements.action === 'revoke') {
      this.unAssignPlaylists(data).subscribe(() => {
        BookrawAppModule.showSuccessMessage('Playlist(s) remove successfully.');
        this.loaderService.close();
        if (selection.selected.length === 1) studentListDialog.reloadList();
      }, (error) => {
        loading = false;
        showErrorMessage(error);
        this.loaderService.close();
      });
    }
  }
}
